import { createSlice, type StateUpdater } from '../../../canvas/core';
import type { PageId } from '../../../document-canvas';

export type PageSelection = {
  partIndex: number;
  pageIds: PageId[];
};

export type PageSelectionSlice = {
  pageSelection: {
    state: {
      selection: PageSelection | null;
    };
    actions: {
      selectPages: (partIndex: number, pageIds: PageId[]) => void;
      togglePageSelection: (partIndex: number, pageId: PageId) => void;
      clearSelection: () => void;
    };
  };
};

export type PageSelectionDeps = Record<string, never>;

const areSelectionsEqual = (
  a: PageSelection | null,
  b: PageSelection | null
) => {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return (
    a.partIndex === b.partIndex &&
    a.pageIds.length === b.pageIds.length &&
    a.pageIds.every(id => b.pageIds.includes(id))
  );
};

export const selectPages =
  <Root extends PageSelectionSlice>(
    partIndex: number,
    pageIds: PageId[]
  ): StateUpdater<Root> =>
  state => {
    const nextSelection: PageSelection | null =
      pageIds.length === 0 ? null : { partIndex, pageIds };

    if (
      areSelectionsEqual(state.pageSelection.state.selection, nextSelection)
    ) {
      return state;
    }

    return {
      ...state,
      pageSelection: {
        ...state.pageSelection,
        state: {
          ...state.pageSelection.state,
          selection: nextSelection,
        },
      },
    };
  };

export const togglePageSelection =
  <Root extends PageSelectionSlice>(
    partIndex: number,
    pageId: PageId
  ): StateUpdater<Root> =>
  state => {
    const current = state.pageSelection.state.selection;

    // Different (or no) part selected — the click replaces the selection.
    if (!current || current.partIndex !== partIndex) {
      return selectPages<Root>(partIndex, [pageId])(state);
    }

    const isSelected = current.pageIds.includes(pageId);
    const nextPageIds = isSelected
      ? current.pageIds.filter(id => id !== pageId)
      : [...current.pageIds, pageId];

    return selectPages<Root>(partIndex, nextPageIds)(state);
  };

export const clearSelection =
  <Root extends PageSelectionSlice>(): StateUpdater<Root> =>
  state => {
    if (state.pageSelection.state.selection === null) return state;

    return {
      ...state,
      pageSelection: {
        ...state.pageSelection,
        state: {
          ...state.pageSelection.state,
          selection: null,
        },
      },
    };
  };

export const createPageSelectionSlice =
  createSlice<PageSelectionDeps>()<PageSelectionSlice>((set, _get, _api) => ({
    pageSelection: {
      state: {
        selection: null,
      },

      actions: {
        selectPages: (partIndex, pageIds) => {
          set(selectPages(partIndex, pageIds));
        },

        togglePageSelection: (partIndex, pageId) => {
          set(togglePageSelection(partIndex, pageId));
        },

        clearSelection: () => {
          set(clearSelection());
        },
      },
    },
  }));
